import { DEV_MODE, getId } from '../game/constants';
import { getDevEmitter, dev_event } from './devEvents';

import {
  Entities,
  EntityKind,
  StaticWall,
  Crate,
  PressurePlate,
  WinLevel,
  Door,
  MeleeEnemy,
  AiType,
  Empty,
  PlayerBody,
  ExtendedBlock,
  LazorToggle,
} from '../game/state/entityKinds';
import { Dir } from '../game/entities/Player';
import { DatabaseHandle } from '../game/database/database';

export enum EditorModes {
  Play,
  Edit,
}

export enum PaintMode {
  Pencil,
  Eraser,
  Select,
}

// Creates default entities
export const getDefaultEntity = {
  [EntityKind.StaticWall]: (): StaticWall => ({
    kind: EntityKind.StaticWall,
    x: -1,
    y: -1,
    id: -1,
  }),
  [EntityKind.Empty]: (): Empty => ({
    kind: EntityKind.Empty,
    x: -1,
    y: -1,
    id: -1,
  }),
  [EntityKind.Crate]: (): Crate => ({
    kind: EntityKind.Crate,
    x: -1,
    y: -1,
    id: -1,
    explodes: false,
    reflects: '',
    breaks: true,
  }),
  [EntityKind.PressurePlate]: (): PressurePlate => ({
    kind: EntityKind.PressurePlate,
    x: -1,
    y: -1,
    id: -1,
    triggerId: -1,
  }),
  [EntityKind.WinLevel]: (): WinLevel => ({
    kind: EntityKind.WinLevel,
    x: -1,
    y: -1,
    id: -1,
    transitionToLevelKey: '', // Empty takes back to overworld
    transitionHint: '', // Hint for what transition to use.
    levelNumber: -1,
  }),
  [EntityKind.Door]: (): Door => ({
    kind: EntityKind.Door,
    x: -1,
    y: -1,
    id: -1,
    triggerIdListener: -1,
    isOpen: false,
    isOpposite: false,
  }),
  [EntityKind.MeleeEnemy]: (): MeleeEnemy => ({
    kind: EntityKind.MeleeEnemy,
    x: -1,
    y: -1,
    id: -1,
    dir: Dir.Right,
    exhaustedTurn: false,
    aiType: AiType.NaiveChase,
  }),
  [EntityKind.PlayerBody]: (): PlayerBody => ({
    kind: EntityKind.PlayerBody,
    x: -1,
    y: -1,
    id: -1,
    dir: Dir.Right,
  }),
  [EntityKind.ExtendedBlock]: (): ExtendedBlock => ({
    kind: EntityKind.ExtendedBlock,
    x: -1,
    y: -1,
    id: -1,
    sharedBlockId: 1,
  }),
  [EntityKind.LazorToggle]: (): LazorToggle => ({
    kind: EntityKind.LazorToggle,
    x: -1,
    y: -1,
    id: -1,
    isActive: false,
    dir: Dir.Right,
    triggerId: -1,
  }),
};

class DevApplicationState {
  private entityToPaint: Entities;
  public currentLevelKey: string;

  contructor() {
    this.currentLevelKey = 'OverWorld';
    this.entityToPaint = getDefaultEntity[EntityKind.StaticWall]();

    setTimeout(() => {
      getDevEmitter()?.on(dev_event.SKIP_TO_LEVEL, (lvl) => {
        this.currentLevelKey = lvl;
      });
    }, 100);

    if (DEV_MODE) {
      window.dev_app = this;
    }
  }

  changeEntityToPaint(e: EntityKind) {
    this.entityToPaint = getDefaultEntity[e]();
    console.log('Changed painting entity:', this.entityToPaint);
    this.changePaintMode(PaintMode.Pencil);
  }

  changeMode(e: EditorModes) {
    if (e === EditorModes.Edit) {
      getDevEmitter()?.emit(dev_event.EDIT_LEVEL, this.currentLevelKey);
    } else if (e === EditorModes.Play) {
      getDevEmitter()?.emit(dev_event.SKIP_TO_LEVEL, this.currentLevelKey);
    } else {
      console.warn('not hooked up yet:', e);
    }
  }

  changePaintMode(e: PaintMode) {
    if (e === PaintMode.Pencil) {
      getDevEmitter()?.emit(dev_event.Paint_pencil, {
        entity: { ...this.entityToPaint, ...{ id: getId() } },
      });
    } else if (e === PaintMode.Select) {
      getDevEmitter()?.emit(dev_event.Paint_Select);
    } else {
      getDevEmitter()?.emit(dev_event.Paint_eraser);
    }
  }

  saveLevel(asFile: boolean = false) {
    getDevEmitter()?.emit(dev_event.Save_Editor_Level, asFile);
  }

  /**
   * Command for quickly nudging the level around. Will destroy any tiles that
   * fall off the screen.
   */
  commandShiftLevelXY(x: number, y: number) {
    DatabaseHandle.getLevel(this.currentLevelKey).then((data: any) => {
      for (const entity of data.entities as Entities[]) {
        entity.x += x;
        entity.y += y;
      }
      const entities = (data.entities as Entities[]).filter(({ x, y }) => {
        return !(x < 0 || x > 15 || y < 0 || y > 15);
      });
      DatabaseHandle.setLevel(this.currentLevelKey, entities).then((_) => {
        getDevEmitter()?.emit(dev_event.SKIP_TO_LEVEL, this.currentLevelKey);
      });
    });
  }

  deleteLocalChanges() {
    if (confirm('Are you sure you want to lose local level work?')) {
      DatabaseHandle.destroyLocalDB();
    }
  }

  help() {
    console.log(`DEV HELP.
You can run commands directly on the level via the console.

	dev_app.commandShiftLevelXY(x, y) - Will move the level x and y and delete anything off the screen. Example: "dev_app.commandShiftLevelXY(1, 1)" will move level 1 tile right and down.

		`);
  }
}

let dev: DevApplicationState | null = null;
if (DEV_MODE) {
  dev = new DevApplicationState();
}

export const getDevApplicationState = () => {
  window.dev_app = dev;
  return dev;
};
